import {groupBy, uniqBy} from 'lodash/fp';

import {RegularTreeItemWithChildren} from 'utils/arrayUtils';

import {TreeBasicNode, TreeKey} from 'components/Tree/TreeTypes';

export function groupTreeItems<T extends {id: string}>({
  getItemsByParentId,
  dataMap,
  ids,
}: {
  getItemsByParentId: (parentId?: string | null) => T[];
  dataMap: Map<string, T>;
  ids: string[];
}): T[] {
  const items = ids.flatMap(id => {
    const item = dataMap.get(id);

    return item ? [item] : [];
  });

  const byParent = groupBy('parentId', items);

  let hasGroupedSmth = false;
  const result = Object.keys(byParent).flatMap(parentId => {
    const parent = dataMap.get(parentId);
    const group = byParent[parentId];
    const fullGroup = getItemsByParentId(parentId);

    if (parent && fullGroup.length === group.length) {
      hasGroupedSmth = true;
      return [parent];
    }

    return group;
  });

  if (!hasGroupedSmth) {
    return result;
  }

  return groupTreeItems({
    dataMap,
    getItemsByParentId,
    ids: result.map(item => item.id),
  });
}

export function getParentChain<T extends {parentId?: string}>(id: string, dataMap: Map<string, T>): string[];
export function getParentChain<T extends {parent?: T | null}>(item: T): T[];

export function getParentChain(a: any, b?: any): any {
  if (b) {
    const id: string = a;
    const dataMap: Map<string, {parentId?: string}> = b;
    const parentId = dataMap.get(id)?.parentId;

    if (parentId) {
      return [...getParentChain(parentId, dataMap), id];
    }

    return [id];
  } else {
    const item: {parent?: any | null} = a;

    if (item.parent) {
      return [...getParentChain(item.parent), item];
    }

    return [item];
  }
}

export function findItemPath<T extends RegularTreeItemWithChildren>(tree: T[], id: string): string[] | undefined {
  for (const item of tree) {
    if (item.id === id) {
      return [id];
    }

    if (item.children) {
      const childPath = findItemPath(item.children, id);

      if (childPath) {
        return [item.id, ...childPath];
      }
    }
  }
}

type ItemWithParent = {parent?: ItemWithParent | null; id: string};

export function getTopParents<T extends ItemWithParent>(tree: T[]) {
  return uniqBy('id', tree.map(getTopParent));
}

function getTopParent<T extends ItemWithParent>(item: T): T {
  if (item.parent) {
    return getTopParent(item.parent) as T;
  }

  return item;
}

export function filterTree<T extends {children?: T[] | null}>(tree: T[], iterator: (item: T) => boolean): T[] {
  return tree.flatMap(item => {
    if (!iterator(item)) {
      return [];
    }

    if (!item.children) {
      return [item];
    }

    return [{...item, children: filterTree(item.children as T[], iterator)}];
  });
}

export function arrToSimpleTree<T>(arr: T[]) {
  return arr.reduceRight((acc, cur) => {
    return {
      ...cur,
      children: acc ? [acc] : undefined,
    };
  });
}

type MaxNestingLevel = 4;
type Increment<T = 0> = T extends 0 ? 1 : T extends 1 ? 2 : T extends 3 ? 4 : T extends 4 ? 5 : T;

export type GenericTreeNode<T, Level = 0> = T & {
  children?: (Level extends MaxNestingLevel ? undefined : GenericTreeNode<T, Increment<Level>>[]) | null;
};
type GenericTreeNodeWithChildren<T, Level = 0> = T & {children: GenericTreeNode<T, Level>[]};

export const hasChildren = <T>(item: T): item is GenericTreeNodeWithChildren<T> => {
  // @ts-expect-error That's because of {} | {children..} typing
  return 'children' in item && item.children?.length;
};

export function iterateTree<T>(
  tree: GenericTreeNode<T>[],
  iterator: (item: GenericTreeNode<T>, parent?: GenericTreeNode<T>) => void,
  options?: {withParent?: boolean}
) {
  function iterateTreeInternal(currentTree: GenericTreeNode<T>[], parent?: GenericTreeNode<T>) {
    return currentTree.forEach(item => {
      iterator(item, parent);

      if (hasChildren(item)) {
        iterateTreeInternal(item.children, options?.withParent ? item : undefined);
      }
    });
  }

  return iterateTreeInternal(tree);
}

export function getTreeNodeMap<K extends TreeKey, T extends {key: K}>(tree: GenericTreeNode<T>[]) {
  const map = new Map<K, GenericTreeNode<T>>();
  iterateTree(tree, item => map.set(item.key, item));
  return map;
}

export function transformTree<T, R>(
  tree: GenericTreeNode<T>[],
  iterator: (item: GenericTreeNode<T>) => R
): GenericTreeNode<R>[] {
  return tree.map(item => ({
    ...iterator(item),
    children: hasChildren(item) ? transformTree(item.children, iterator) : undefined,
  }));
}

export function getSelectedLeaves<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  selected: K[] | Set<K>,
  isParentSelected = false
): K[] {
  const selectedSet = Array.isArray(selected) ? new Set(selected) : selected;
  return tree.flatMap(item => {
    const isSelected = isParentSelected || selectedSet.has(item.key);

    if (hasChildren(item)) {
      return getSelectedLeaves(item.children, selectedSet, isSelected);
    }

    return isSelected ? [item.key] : [];
  });
}

export function removeNonLeaveKeys<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  keys: K[] | Set<K>,
  result = new Set(keys)
): Set<K> {
  tree.forEach(item => {
    if (hasChildren(item)) {
      result.delete(item.key);
      removeNonLeaveKeys(item.children, result, result);
    }
  });
  return result;
}

export function addDisabledFlagsToNodes<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  disabled: K[] | Set<K>,
  isParentDisabled = false
): GenericTreeNode<T & {disabled: boolean}>[] {
  const disabledSet = Array.isArray(disabled) ? new Set(disabled) : disabled;
  return tree.map(item => {
    const isItemDisabled = isParentDisabled || disabledSet.has(item.key);
    const children = hasChildren(item)
      ? addDisabledFlagsToNodes(item.children, disabledSet, isItemDisabled)
      : undefined;
    const isDisabled = isItemDisabled || children?.every(item => item.disabled);

    return {
      ...item,
      children,
      disabled: isDisabled ?? false,
    };
  });
}

export function getAllLeaveKeys<K extends TreeKey = string, T extends {key: K} = {key: K}>(
  tree: GenericTreeNode<T>[],
  result = [] as K[]
): K[] {
  tree.forEach(item => {
    if (hasChildren(item)) {
      getAllLeaveKeys(item.children, result);
    } else {
      result.push(item.key);
    }
  });
  return result;
}

export function inverseSelectedKeys<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  selected: K[] | Set<K>
): K[] {
  const selectedSet = ungroupSelectedKeys(tree, selected);
  const allKeys = getAllLeaveKeys<K, T>(tree);
  const result = new Set(allKeys);
  allKeys.forEach(key => {
    if (selectedSet.has(key)) {
      result.delete(key);
    }
  });
  return Array.from(result);
}

/**
 * recursively marks parent selected if all its children selected
 */
export function groupSelectedKeys<K extends TreeKey = string, T extends {key: K} = {key: K}>(
  tree: GenericTreeNode<T>[],
  selected: K[] | Set<K>
): Set<K> {
  const result = new Set(selected);
  tree.forEach(item => getIsNodeSelected(item, result));
  return result;
}

export function getIsNodeSelected<K extends TreeKey = string, T extends {key: K} = {key: K}>(
  node: GenericTreeNode<T>,
  selected: Set<K>
): boolean {
  let isSelected = selected.has(node.key);

  if (!isSelected && hasChildren(node)) {
    let isAllChildrenSelected = true;
    node.children.forEach(child => {
      if (!getIsNodeSelected(child, selected)) {
        isAllChildrenSelected = false;
      }
    });

    if (isAllChildrenSelected) {
      isSelected = true;
      selected.add(node.key);
      node.children.forEach(child => selected.delete(child.key));
    }
  }

  return isSelected;
}

export function getIsAllSelected<K extends TreeKey, T extends {key: K}>(
  nodes: GenericTreeNode<T>[],
  selected: Set<K>
): boolean {
  return nodes.every(node => getIsNodeSelected(node, selected));
}

/**
 * recursively marks leaves selected instead of their parents
 */
export function ungroupSelectedKeys<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  selected: K[] | Set<K>,
  result = new Set(selected)
): Set<K> {
  tree.forEach(item => {
    if (hasChildren(item)) {
      if (result.has(item.key)) {
        result.delete(item.key);
        item.children.forEach(child => result.add(child.key));
      }

      ungroupSelectedKeys(item.children, selected, result);
    }
  });
  return result;
}

export function groupSelectedKeysByLevel<K extends TreeKey, T extends {key: K}>(
  tree: GenericTreeNode<T>[],
  selected: K[] | Set<K>,
  level = 0,
  result: K[][] = []
): K[][] {
  const selectedSet = Array.isArray(selected) ? new Set(selected) : selected;
  tree.forEach(item => {
    if (selectedSet.has(item.key)) {
      result[level] = result[level] || [];
      result[level].push(item.key);
    } else if (hasChildren(item)) {
      groupSelectedKeysByLevel(item.children, selectedSet, level + 1, result);
    }
  });
  return result;
}

export function filterTreeByMatchKeys<K extends TreeKey, T extends TreeBasicNode<K>>(
  tree: GenericTreeNode<T>[],
  matchKeys: Set<K>
) {
  function filterTreeByKeys(tree: GenericTreeNode<T>[]): GenericTreeNode<T>[] {
    function traverse(node: GenericTreeNode<T>): GenericTreeNode<T> | null {
      if (matchKeys.has(node.key)) {
        return node;
      }

      const filteredChildren =
        node.children?.map(traverse).filter((child): child is GenericTreeNode<T> => child !== null) || [];

      if (filteredChildren.length > 0) {
        return {...node, children: filteredChildren};
      }

      return null;
    }

    return tree.map(traverse).filter((node): node is GenericTreeNode<T> => node !== null);
  }

  return filterTreeByKeys(tree);
}
